// What Account reads and writes, as the member themselves (docs/data-model.md 6 and 10.1).
// - Reads: their own people row, each product's terms line (catalogue.terms_line, from terms 2.2),
//   an email change still waiting for its code (hub.my_email_change), and whether their login is
//   shared with POYT's site now (hub.my_login_shared: people.login_shared, or POYT's own site's
//   tables while they're there; since migration 82 the Accelerator's old site, which forwards to the
//   hub, counts only while the Accelerator hasn't moved in). Row level security decides what comes
//   back; My programmes takes its products from the Library's own rows.
// - Writes straight to the tables, under the policies: the profile (full_name and location, and for
//   Accelerator members about and directory_opt_in) on people, another mastermind's directory switch
//   (its row in directory_listings, migration 20261013200000), and a data request. The person row
//   carries about, directory_opt_in and photo_path for Profile's Accelerator parts (photo_path only
//   says there's a photo to sign: sign-upload alone sets it). first_name follows full_name on the server and is never sent. The 3
//   email switches went with the notification emails (Adam, 5 Oct): the columns are dropped.
// - The 3 logged-in functions: set-password, change-email and log-out-everywhere. Each refuses
//   while staff view as a member, and every refusal carries the line to show.
import { supabase, must } from '../lib/supabase.js'

// Their certificates (not revoked: row level security returns only those), for My programmes
export const CERTIFICATE_COLUMNS = 'id, number, check_path, product_id, course_name'

export const PERSON_COLUMNS = 'id, full_name, first_name, email, location, about, directory_opt_in, photo_path, password_set_at, login_shared'

export async function loadAccount(personId) {
  const [person, terms, change, shared, certificates] = await Promise.all([
    supabase.from('people').select(PERSON_COLUMNS).eq('id', personId).single().then(must),
    supabase.from('catalogue').select('id, terms_line').then(must),
    supabase.rpc('my_email_change').then(must),
    supabase.rpc('my_login_shared').then(must),
    // a certificate that didn't load leaves My programmes as it is, never the whole page
    supabase.from('certificates').select(CERTIFICATE_COLUMNS).eq('person_id', personId).order('id')
      .then(r => (r.error ? [] : r.data || [])),
  ])
  return {
    person,
    terms: Object.fromEntries((terms || []).map(t => [t.id, t.terms_line])),
    emailChange: (change && change[0]) || null,
    shared: shared === true || person?.login_shared === true,
    certificates,
  }
}

// Only what Account may change: full_name and location, and about and directory_opt_in. 0 rows back means the write changed
// nothing (staff viewing as a member), which is a refusal, not a save.
export async function savePerson(personId, patch) {
  const rows = await supabase.from('people').update(patch).eq('id', personId).select(PERSON_COLUMNS).then(must)
  if (!rows || !rows.length) throw new Error('nothing saved')
  return rows[0]
}

// The other masterminds' directory switches (the product-types follow-ups, 13 Oct): the products whose
// member directory they show in, from their own rows in hub.directory_listings (row level security returns
// only theirs, or the member's while staff view as them). The Property Accelerator's switch stays
// people.directory_opt_in, which the database keeps in step with its row, so Account reads and writes it
// as before.
export async function loadListings(personId) {
  const rows = await supabase.from('directory_listings').select('product_id').eq('person_id', personId).then(must)
  return (rows || []).map(r => r.product_id)
}

// 1 mastermind's switch, on or off: their row added or taken away. A row already there (23505) is the
// switch as asked; a refusal (staff viewing as a member) throws.
export async function setListing(personId, productId, on) {
  if (on) {
    const { error } = await supabase.from('directory_listings').insert({ person_id: personId, product_id: productId })
    if (error && error.code !== '23505') throw error
    return
  }
  await supabase.from('directory_listings').delete().eq('person_id', personId).eq('product_id', productId).then(must)
}

// Their latest export Adam has made, if it's still downloadable (7 days from when it was made:
// sign-file refuses after that), or null. A read that fails leaves Your data as it was.
export const EXPORT_DAYS = 7
export async function loadDataExport(personId, nowMs = Date.now()) {
  const since = new Date(nowMs - EXPORT_DAYS * 86400000).toISOString()
  const { data, error } = await supabase.from('data_requests').select('id, handled_at')
    .eq('person_id', personId).eq('kind', 'export').eq('status', 'done').gte('handled_at', since)
    .order('handled_at', { ascending: false }).limit(1)
  return error ? null : (data && data[0]) || null
}

// A data request: 'export' or 'delete'. 23505 means one of that kind is already open, which the
// member hears about the same way as a new one.
export async function askForData(personId, kind) {
  const { error } = await supabase.from('data_requests').insert({ person_id: personId, kind })
  if (error && error.code !== '23505') throw error
}

// The 3 logged-in functions go through lib/functions.js, whose HubFunctionError carries the
// function's own code, its line to show (message) and the whole answer (body).
export { callHubFunction as callMember, HubFunctionError } from '../lib/functions.js'
